import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as UserUploadService from "../../services/UserUploadService";
import * as UserUploadConstants from "../../constants/UserUploadConstants";
import { saveFile } from "../../helpers/SaveFile";
import { readErrorBody } from "../../helpers/ErrorMessage";
import {
  userUploadDetailsFail,
  userUploadDetailsRequest,
  userUploadDetailsSuccess,
  userUploadDownloadFail,
  userUploadDownloadRequest,
  userUploadDownloadSuccess,
  userUploadFail,
  userUploadListFail,
  userUploadListRequest,
  userUploadListSuccess,
  userUploadPollFail,
  userUploadPollRequest,
  userUploadPollSuccess,
  userUploadRequest,
  userUploadSuccess,
} from "../slices/userUploadSlice";

// CustomResponseMiddleware rewrites every 400 to
// { response: "<ErrorCode>", values, status, code: "BAD_REQUEST", ... },
// keeping only ErrorCode and Values; other errors may still carry
// { errorCode, message }. A 403 from the permission check has no body.
const detailOf = (data) => {
  if (typeof data?.message === "string") {
    return data.message;
  }
  if (typeof data?.values === "string") {
    return data.values;
  }
  return Array.isArray(data?.values) ? data.values.join(", ") : null;
};

export const errorOf = (error) => {
  const status = error?.response?.status || 400;
  const data = error?.response?.data;
  let code =
    data?.errorCode ||
    (typeof data?.response === "string" ? data.response : null);
  if (!code) {
    code = status === 403 ? "FORBIDDEN" : "SOMETHING_ELSE_WRONG";
  }
  return { status, code, message: detailOf(data) };
};

export function* uploadUsers({ payload }) {
  try {
    const ajax = yield call(UserUploadService.uploadUsers, payload.file);
    yield put(userUploadSuccess(ajax.data));
  } catch (error) {
    yield put(userUploadFail(errorOf(error)));
  }
}

// Polls leave the rows out; the dialog asks for them once the status is
// final (includeRows: true).
export function* pollUpload({ payload }) {
  try {
    const ajax = yield call(UserUploadService.getUserUpload, payload.id, {
      includeRows: payload.includeRows === true,
    });
    yield put(userUploadPollSuccess(ajax.data));
  } catch (error) {
    yield put(userUploadPollFail(errorOf(error)));
  }
}

export function* loadUploads({ payload }) {
  try {
    const ajax = yield call(UserUploadService.getUserUploads, payload);
    yield put(userUploadListSuccess(ajax.data));
  } catch (error) {
    yield put(userUploadListFail(errorOf(error)));
  }
}

export function* loadUpload({ payload }) {
  try {
    const ajax = yield call(UserUploadService.getUserUpload, payload.id);
    yield put(userUploadDetailsSuccess(ajax.data));
  } catch (error) {
    yield put(userUploadDetailsFail(errorOf(error)));
  }
}

// A download is requested as a file, so the API's error body arrives as a
// Blob: it is read for the API's code. UPLOAD_FILES_EXPIRED (deleted after
// 90 days) and UPLOAD_FILES_MISSING (the stored files are gone) each have
// their own message; any other 404 (for example a results file not made
// yet, or a body that cannot be read) is a download that failed
// (UPLOAD_DOWNLOAD_FAILED). Every 404 used to be read as expired, so files
// gone before the 90 days showed the 90-day message (code review phase 2).
export function* downloadFile({ payload }) {
  const serviceCall =
    payload.kind === UserUploadConstants.USER_UPLOAD_FILE_ORIGINAL
      ? UserUploadService.downloadUploadOriginal
      : UserUploadService.downloadUploadResults;
  try {
    const ajax = yield call(serviceCall, payload.id);
    yield call(saveFile, ajax.data, payload.fileName);
    yield put(userUploadDownloadSuccess());
  } catch (error) {
    const body = yield call(readErrorBody, error?.response?.data);
    const failure = errorOf({
      response: { status: error?.response?.status, data: body },
    });
    yield put(
      userUploadDownloadFail(
        failure.status === 404 &&
          !UserUploadConstants.USER_UPLOAD_ERROR_MESSAGES[failure.code]
          ? { ...failure, code: "UPLOAD_DOWNLOAD_FAILED" }
          : failure
      )
    );
  }
}

export default function* userUploadSaga() {
  yield all([
    takeLatest(userUploadRequest.type, uploadUsers),
    takeLatest(userUploadPollRequest.type, pollUpload),
    takeLatest(userUploadListRequest.type, loadUploads),
    takeLatest(userUploadDetailsRequest.type, loadUpload),
    takeEvery(userUploadDownloadRequest.type, downloadFile),
  ]);
}
